'use client';
/**
 * LE RELEVÉ MENSUEL DES NON-CONFORMITÉS adressé à un correspondant — depuis sa fiche de l'ANNUAIRE.
 *
 * Repris tel quel de l'ancien écran du carnet de contacts (supprimé le 20/10/2026, Ines : « supprimer totalement
 * le carnet et refaire les liens »). Demande d'origine (RQ, 07/10/2026) : « générer la liste des NC du mois pour ce
 * correspondant et la lui envoyer », puis « simplement pouvoir imprimer un pdf ».
 *
 *  · UN RELEVÉ TÉLÉCHARGÉ N'EST PAS UN RELEVÉ ENVOYÉ : « Télécharger le PDF » et « Envoyer par courriel » sont deux
 *    gestes nommés ; le registre des relevés dit lequel a eu lieu.
 *  · UN ENVOI QUI NE PARTIRA PAS N'EST PAS PROPOSÉ : bouton grisé, et la raison dite (aucune adresse « qualité »,
 *    aucun serveur de courriel).
 *  · UN RELEVÉ VIDE N'EST PAS UNE ERREUR : « aucune non-conformité ne lui est imputée sur ce mois » est un résultat.
 *  · Mois, période ou sélection : la sélection ne propose que les NC de CE correspondant (le serveur refuse les autres).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { I, card, inputStyle, EmptyState, FieldError, useBusy, Help, useConfirmation, Pastille } from '@/components/ui';
import { lbl } from '../ressources/commun';
import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { Tableau } from '@/shared/Tableau';

/** Le correspondant tel que le serveur le sert (`GET ged/correspondants?entite=…`) : une fiche de l'annuaire. */
export type Ligne = {
  documentId: string; code: string; nom: string; actif: boolean; familles: string[]; manques: string[];
  destinatairesQualite: { adresses: string[]; usage: string | null; repli: boolean };
  compte: { ncs: number; dossiers: number; ouvertes: number; signalees?: number; reclamations?: number };
};
type LigneReleve = {
  ncRef: string; ncTitre: string; date: string; gravite: string; statut: string;
  dossier: string; suites: string;
};
type ModePeriode = 'mois' | 'plage' | 'selection';
type Releve = {
  correspondant: { code: string; nom: string };
  mois: string;
  periode: { mode: ModePeriode; du: string; au: string; ncRefs: string[]; libelle: string; cle: string };
  lignes: LigneReleve[];
  compte: {
    ncs: number; dossiers: number; parGravite: Record<string, number>;
    moisPrecedents: { mois: string; ncs: number; dossiers: number }[];
  };
};
type NcImputee = { ref: string; titre: string; date: string; gravite: string; statut: string; dossiers: number };
type DossierReleve = {
  releve: Releve;
  correspondant: Ligne;
  moisLabel: string;
  titre: string;
  phraseVide: string;
  /** Les NC imputées à ce correspondant — ce qu'on peut cocher en mode sélection. */
  imputees: NcImputee[];
  anterieurs: {
    ref: string; mois: string; periode: string; mode: ModePeriode; canal: string; envoye: boolean;
    datePrepare: string; dateEnvoi: string; destinataires: string[]; dossiers: number | null;
  }[];
  /* La forme EXACTE de `etatCourriel` (`backend/src/courriel-etat.ts`) :
     `configure` et `consequence`. Les renommer ici au jugé a eu un effet
     silencieux et grave, corrigé le 07/10/2026 avant mise en service :
     `pret !== false` vaut VRAI sur un champ absent, donc le bouton d'envoi
     restait actif alors qu'aucun serveur n'est réglé. Un bouton qui promet un
     envoi impossible est précisément le défaut que tout le lot des
     notifications existe pour fermer. */
  courriel: { configure: boolean; consequence: string; manques: string[] };
  /** Ce qui a DÉJÀ été adressé pour cette période précise — l'avertissement se
   *  lit AVANT le clic, pas après (arbitrage du 08/10/2026). */
  dejaAdresse: { existe: boolean; phrase: string };
  peutEcrire: boolean;
};

const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5,
  color: 'var(--text-body)', lineHeight: 1.5,
};

const AIDE_PERIODE = 'Mois : le relevé mensuel, avec le rappel des trois mois précédents. Période : du … au …, '
  + 'bornes incluses. Sélection : les non-conformités que vous cochez, avec tous leurs dossiers, quelle que '
  + 'soit leur date.';

/** Le mois précédent celui d'aujourd'hui — le relevé se fait « à la fin du mois ». */
function moisParDefaut(aujourdhui: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(aujourdhui);
  if (!m) return '';
  const an = Number(m[1]); const mo = Number(m[2]);
  return mo <= 1 ? `${an - 1}-12` : `${an}-${String(mo - 1).padStart(2, '0')}`;
}

export function PanneauReleve({ ligne, aujourdhui, onFerme }: {
  ligne: Ligne; aujourdhui: string; onFerme: () => void;
}) {
  const { showToast } = useGed();
  const [mode, setMode] = useState<ModePeriode>('mois');
  const [mois, setMois] = useState(moisParDefaut(aujourdhui));
  const [du, setDu] = useState(`${moisParDefaut(aujourdhui)}-01`);
  const [au, setAu] = useState(aujourdhui);
  const [choix, setChoix] = useState<string[]>([]);
  const [d, setD] = useState<DossierReleve | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState('');
  const [tour, setTour] = useState(0);
  const { run, estOccupe } = useBusy();
  /* ⚠️ `{confirmation}` DOIT être rendu (voir plus bas), sinon `demander`
     attend une réponse que rien n'affiche et le geste reste suspendu. */
  const { demander, confirmation } = useConfirmation();

  /* Remise à l'état « en chargement » PENDANT LE RENDU, et non dans l'effet :
     un `setState` dans le corps d'un effet déclenche un rendu en cascade
     (React 19), et la règle est une ERREUR de lint dans ce dépôt. Changer de
     mois doit effacer le relevé précédent — sinon on lit octobre sous le titre
     de septembre pendant le temps de l'appel. */
  /* La période telle que le serveur la lit — la même pour l'aperçu, le CSV,
     le PDF et le courriel : un seul objet, sinon l'aperçu et le document
     pourraient ne pas couvrir la même chose. */
  const periode = mode === 'mois' ? { mode, mois }
    : mode === 'plage' ? { mode, du, au }
      : { mode, ncRefs: choix };
  const requete = new URLSearchParams(
    mode === 'mois' ? { mode, mois } : mode === 'plage' ? { mode, du, au } : { mode, ncRefs: choix.join(',') },
  ).toString();
  /* Une plage inversée ne part même pas au serveur : il la refuserait, mais
     l'écran peut le dire tout de suite, à côté des deux champs. */
  const plageInvalide = mode === 'plage' && (!du || !au || du > au);
  const selectionVide = mode === 'selection' && choix.length === 0;

  const cle = `${ligne.documentId}|${requete}|${tour}`;
  const [vue, setVue] = useState(cle);
  if (vue !== cle) { setVue(cle); setD(null); setChargement(!plageInvalide); setEchec(''); }

  useEffect(() => {
    let vivant = true;
    if (plageInvalide) return () => { vivant = false; };
    void fetch(`/api/ged/ged/correspondants/${ligne.documentId}/releve?${requete}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) return r.json();
        const j = await r.json().catch(() => null);
        throw new Error(j?.error?.message || '');
      })
      .then((j) => { if (vivant) { setD(j); if (!j) setEchec('Le relevé n’a pas pu être calculé.'); } })
      .catch((e: Error) => { if (vivant) setEchec(e.message || 'Le relevé n’a pas pu être calculé.'); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [ligne.documentId, requete, tour, plageInvalide]);

  const r = d?.releve;
  const dest = ligne.destinatairesQualite;
  const courrielPret = d?.courriel?.configure === true;
  /* Le bouton grisé DIT pourquoi : c'est exactement ce qui manquait aux deux
     flux publics avant le lot des notifications — on cliquait, l'écran
     remerciait, et rien ne partait. */
  /* Produire et adresser le relevé : le RQ et le CQ (le serveur fait foi). Un
     correspondant désactivé n'en reçoit plus (14/10/2026). */
  const peutAdresser = d?.peutEcrire === true;
  const desactive = ligne.actif === false;
  const empechement = desactive
    ? 'Ce correspondant est désactivé : on ne lui adresse plus de relevé. Réactivez-le d’abord si c’est voulu.'
    : !dest.adresses.length
    ? 'Ce correspondant n’a aucune adresse de courriel : renseignez-en une dans sa fiche, ou téléchargez le PDF.'
    : !courrielPret
      ? `Aucun serveur d’envoi n’est réglé — ${d?.courriel?.consequence || 'rien ne partira'}. Le PDF reste disponible.`
      : '';

  /**
   * Un geste d'écriture, qui ACCEPTE d'être renvoyé une seconde fois.
   *
   * Le serveur répond 409 quand un relevé existe déjà pour cette période : on
   * pose alors la question en nommant ce qui est déjà parti, et l'on rejoue le
   * même appel avec `confirmerSecond`. Le drapeau porte sur CE geste-ci, jamais
   * sur un réglage durable — « ne plus me prévenir » aurait éteint
   * l'avertissement aussi pour les doublons qui sont des fautes.
   */
  const ecrire = async (chemin: string) => {
    const appel = (second: boolean) => fetchEcriture(
      `/api/ged/ged/correspondants/${ligne.documentId}/releve/${chemin}`,
      {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(second ? { ...periode, confirmerSecond: true } : periode),
      },
    );
    const rep = await appel(false);
    if (rep.status !== 409) return rep;
    const j = await rep.json().catch(() => null);
    const ok = await demander(j?.error?.message || 'Un relevé a déjà été adressé pour cette période. En adresser un second ?',
      { titre: 'Relevé déjà adressé', confirmer: 'Adresser quand même' });
    if (!ok) return null;
    return appel(true);
  };

  const telecharger = () => run('pdf', async () => {
    const rep = await ecrire('pdf');
    if (!rep) return;
    if (!rep.ok) {
      const j = await rep.json().catch(() => null);
      showToast(j?.error?.message
        || 'Le PDF n’a pas pu être produit. Le relevé reste consultable ici et exportable en CSV.', 'erreur');
      return;
    }
    const blob = await rep.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `releve-${ligne.code}-${d?.releve.periode.cle || mois}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('Relevé produit — il est enregistré au registre des relevés, et les non-conformités portent la trace.', 'info');
    setTour((t) => t + 1);
  });

  const envoyer = () => run('mail', async () => {
    const rep = await ecrire('envoyer');
    if (!rep) return;
    const j = await rep.json().catch(() => null);
    if (!rep.ok) { showToast(j?.error?.message || 'L’envoi n’a pas abouti.', 'erreur'); return; }
    /* Les DEUX faits : ce qui est parti et ce qui ne l'est pas. Un message qui
       ne dirait que le succès laisserait un échec partiel invisible. */
    const parts = [
      j?.envoyes?.length ? `Envoyé à ${j.envoyes.join(', ')}.` : 'Aucun envoi n’a abouti.',
      j?.echecs?.length ? `Refusé pour ${j.echecs.map((e: { adresse: string; motif: string }) => `${e.adresse} (${e.motif})`).join(' ; ')}.` : '',
      j?.repli ? 'Adresse de repli utilisée — aucune adresse « qualité » n’est déclarée.' : '',
      j?.pdfJoint === false ? 'Le PDF n’a pas pu être joint : le corps du message porte le tableau.' : '',
    ].filter(Boolean);
    showToast(parts.join(' '), j?.ok ? 'info' : 'erreur');
    setTour((t) => t + 1);
  });

  /* Même règle que le formulaire : le panneau s'ouvre en haut de page alors que
     la ligne cliquée est plus bas — on l'amène à l'écran. */
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => { racine.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [ligne.documentId]);

  /* La clé joint la référence et le rang : deux dossiers de la MÊME
     non-conformité partageraient sinon une clé, et React réutiliserait la
     mauvaise ligne au changement de mois. */
  const lignesReleve = useMemo(() => (r?.lignes || []).map((l, i) => ({ ...l, cle: `${l.ncRef}-${i}` })), [r]);

  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Relevé — {ligne.nom}
        </h2>
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={onFerme}>Fermer</Bouton>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'grid', gap: 5 }}>
          <span style={lbl}><Help aide={AIDE_PERIODE}>Ce qu’on adresse</Help></span>
          <Segment ariaLabel="Période du relevé" valeur={mode} onChange={setMode}
            options={[
              { valeur: 'mois', libelle: 'Mois' },
              { valeur: 'plage', libelle: 'Période' },
              { valeur: 'selection', libelle: 'Sélection', compteur: choix.length || undefined },
            ]} />
        </div>
        {mode === 'mois' && (
          <div>
            <label style={lbl} htmlFor="rel-mois">Mois</label>
            <input id="rel-mois" type="month" value={mois} onChange={(e) => setMois(e.target.value)}
              style={{ ...inputStyle, maxWidth: 190 }} />
          </div>
        )}
        {mode === 'plage' && (<>
          <div>
            <label style={lbl} htmlFor="rel-du">Du</label>
            <input id="rel-du" type="date" value={du} max={au || undefined} onChange={(e) => setDu(e.target.value)}
              style={{ ...inputStyle, maxWidth: 170 }} />
          </div>
          <div>
            <label style={lbl} htmlFor="rel-au">Au (inclus)</label>
            <input id="rel-au" type="date" value={au} min={du || undefined} onChange={(e) => setAu(e.target.value)}
              style={{ ...inputStyle, maxWidth: 170 }} />
          </div>
        </>)}
        <span style={{ flex: 1 }} />
        {peutAdresser && (
          <Bouton taille="sm" variante="primaire" chargement={estOccupe('pdf')}
            disabled={estOccupe() || chargement || plageInvalide || selectionVide || desactive}
            title={desactive ? empechement : selectionVide ? 'Cochez au moins une non-conformité.' : plageInvalide ? 'La période est incomplète ou inversée.' : undefined}
            onClick={() => void telecharger()}>
            <I n="download" s={13} />Télécharger le PDF
          </Bouton>
        )}
        <Bouton taille="sm" disabled={estOccupe() || chargement || plageInvalide || selectionVide}
          onClick={() => {
            window.location.href = `/api/ged/ged/correspondants/${ligne.documentId}/releve.csv?${requete}`;
          }}>
          Exporter (CSV)
        </Bouton>
        {peutAdresser && (
          <Bouton taille="sm" chargement={estOccupe('mail')}
            disabled={estOccupe() || chargement || !!empechement || plageInvalide || selectionVide}
            title={empechement || (selectionVide ? 'Cochez au moins une non-conformité.' : `Envoyer à ${dest.adresses.join(', ')}`)}
            onClick={() => void envoyer()}>
            <I n="mail" s={13} />Envoyer par courriel
          </Bouton>
        )}
      </div>

      {plageInvalide && <div style={encart}>La période doit avoir un premier et un dernier jour, dans l’ordre.</div>}

      {mode === 'selection' && d && !chargement && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span style={lbl}>Non-conformités imputées à {ligne.nom}</span>
            <span style={{ flex: 1 }} />
            {d.imputees.length > 0 && (
              <Bouton taille="sm" variante="discret"
                onClick={() => setChoix(choix.length === d.imputees.length ? [] : d.imputees.map((n) => n.ref))}>
                {choix.length === d.imputees.length ? 'Tout décocher' : 'Tout cocher'}
              </Bouton>
            )}
          </div>
          {d.imputees.length === 0 ? (
            <EmptyState titre="Aucune non-conformité ne lui est imputée."
              indice="Une non-conformité s’impute à un correspondant dans sa déclaration (« L’erreur vient-elle d’un correspondant extérieur ? »). Seules les NC retenues figurent au relevé." />
          ) : d.imputees.map((n) => {
            const coche = choix.includes(n.ref);
            return (
              <label key={n.ref} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer',
                padding: '6px 8px', borderRadius: 'var(--radius-sm)',
                background: coche ? 'var(--cyan-50)' : 'transparent',
              }}>
                <input type="checkbox" checked={coche} style={{ marginTop: 3 }}
                  onChange={(e) => setChoix(e.target.checked ? [...choix, n.ref] : choix.filter((x) => x !== n.ref))} />
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, whiteSpace: 'nowrap' }}>{n.ref}</span>
                <span style={{ flex: 1 }}>{n.titre || <em style={{ color: 'var(--text-muted)' }}>sans intitulé</em>}</span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {fmtDate(n.date)} · {n.dossiers ? `${n.dossiers} dossier(s)` : 'non détaillée'}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* ⚠️ L'AVERTISSEMENT SE LIT AVANT LE CLIC. La confirmation du second
          envoi arrive de toute façon au moment du geste, mais la découvrir là
          est trop tard pour changer de période : on vient de choisir un mois
          déjà adressé sans le savoir. */}
      {d?.dejaAdresse?.existe && (
        <div style={encart}>
          <strong style={{ color: 'var(--text-strong)' }}>Déjà adressé.</strong> {d.dejaAdresse.phrase}{' '}
          En produire un second reste possible — il vous sera demandé de le confirmer.
        </div>
      )}
      {empechement && <div style={encart}>{empechement}</div>}
      {dest.repli && !empechement && (
        <div style={encart}>
          Aucune adresse « relevés et signalements qualité » n’est déclarée : l’envoi partirait à
          l’adresse <strong>{dest.adresses.join(', ')}</strong>, qui n’est pas celle de la personne qui
          répond d’un écart.
        </div>
      )}

      {chargement && <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Calcul du relevé…</p>}
      {echec && <div style={encart}>{echec}</div>}

      {r && !chargement && (<>
        <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
          <strong>{d?.titre}</strong> — {r.compte.ncs} non-conformité(s) · {r.compte.dossiers} dossier(s) concerné(s)
          {r.compte.moisPrecedents.length > 0 && (
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}· rappel : {r.compte.moisPrecedents.map((m) => `${m.mois} — ${m.dossiers}`).join(' · ')}
            </span>
          )}
        </div>

        {/* En mode « sélection », un relevé vide ne dit rien : la liste à cocher
            est juste au-dessus. Sinon, le tableau porte son état vide. */}
        {r.lignes.length === 0 && mode === 'selection' ? null : (
          /* L7 vague 2 (11/10/2026) : registre SIMPLE sur le socle. L'export reste
             celui du SERVEUR (« Exporter (CSV) », avec l'en-tête du relevé) : un
             seul export par registre. */
          <Tableau
            id="correspondants-releve"
            ajusterLargeur
            niveau="simple"
            lignes={lignesReleve}
            cleLigne={(l) => l.cle}
            vide={{
              titre: d?.phraseVide || 'Aucune non-conformité ne lui est imputée sur cette période.',
              indice: 'Ce n’est pas une erreur : c’est un résultat, et le document le dira en toutes lettres si vous l’adressez.',
            }}
            colonnes={[
              { cle: 'date', libelle: 'Date', type: 'date', valeur: (l) => l.date, rendu: (l) => fmtDate(l.date) },
              { cle: 'ncRef', libelle: 'Référence', valeur: (l) => l.ncRef, style: { fontFamily: 'var(--font-mono)', fontWeight: 700 }, rendu: (l) => l.ncRef },
              { cle: 'ncTitre', libelle: 'Constat', valeur: (l) => l.ncTitre, rendu: (l) => l.ncTitre },
              { cle: 'dossier', libelle: 'Dossier concerné', valeur: (l) => l.dossier || '', rendu: (l) => l.dossier || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>non détaillé</span> },
              { cle: 'gravite', libelle: 'Gravité', type: 'liste', valeur: (l) => l.gravite, rendu: (l) => l.gravite },
              { cle: 'statut', libelle: 'État', type: 'liste', valeur: (l) => l.statut, rendu: (l) => l.statut },
              { cle: 'suites', libelle: 'Suites', valeur: (l) => l.suites || '', rendu: (l) => l.suites || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>en cours</span> },
            ]}
          />
        )}

        {(d?.anterieurs?.length ?? 0) > 0 && (
          <div style={{ display: 'grid', gap: 5 }}>
            <span style={lbl}>Relevés déjà produits</span>
            {d?.anterieurs.map((a) => (
              <div key={a.ref} style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{a.ref}</span>
                {' — '}{a.periode} · {a.dossiers ?? 0} dossier(s) ·{' '}
                {a.envoye
                  ? <>envoyé le {fmtDate(a.dateEnvoi)} à {a.destinataires.join(', ')}</>
                  : <>PDF produit le {fmtDate(a.datePrepare)} — <span style={{ color: 'var(--text-muted)' }}>remis par le laboratoire, hors plateforme</span></>}
              </div>
            ))}
          </div>
        )}
      </>)}
      {confirmation}
    </div>
  );
}

/**
 * L'onglet « Relevé des NC » d'une fiche de l'annuaire : ce qui est imputé à ce contact, ses adresses « qualité »,
 * et le panneau du relevé (mois, période ou sélection ; PDF ou courriel).
 */
export function ReleveDuContact({ id }: { id: string }) {
  const [etat, setEtat] = useState<{ ligne: Ligne | null; aujourdhui: string; peutReleve: boolean } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/correspondants?entite=${encodeURIComponent(id)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: { lignes: Ligne[]; aujourdhui: string; peutReleve: boolean }) => { if (vivant) setEtat({ ligne: j.lignes[0] || null, aujourdhui: j.aujourdhui, peutReleve: j.peutReleve }); })
      .catch((e: Error) => { if (vivant) setErreur(e.message); });
    return () => { vivant = false; };
  }, [id]);
  if (erreur) return <FieldError>Le relevé n’a pas pu être lu : {erreur}</FieldError>;
  if (!etat) return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Lecture…</div>;
  const l = etat.ligne;
  if (!l) return <p style={{ margin: 0, color: 'var(--text-muted)' }}>Aucune non-conformité ni réclamation n’est rattachée à ce contact.</p>;
  if (ouvert) return <PanneauReleve ligne={l} aujourdhui={etat.aujourdhui} onFerme={() => setOuvert(false)} />;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Pastille ton={l.compte.ncs ? 'warning' : 'neutral'}>{l.compte.ncs} NC imputée(s)</Pastille>
        <Pastille ton="neutral">{l.compte.dossiers} dossier(s)</Pastille>
        {(l.compte.signalees ?? 0) > 0 && <Pastille ton="info">{l.compte.signalees} signalée(s) par lui</Pastille>}
        {(l.compte.reclamations ?? 0) > 0 && <Pastille ton="info">{l.compte.reclamations} réclamation(s)</Pastille>}
      </div>
      <div style={{ fontSize: 13 }}>
        {l.destinatairesQualite.adresses.length
          ? <>Le relevé partira à <strong>{l.destinatairesQualite.adresses.join(', ')}</strong>{l.destinatairesQualite.repli ? ` (adresse « ${l.destinatairesQualite.usage} », faute d’adresse « qualité »)` : ''}.</>
          : <span style={{ color: 'var(--amber-700)' }}>Aucune adresse email : le relevé pourra être téléchargé en PDF, pas envoyé. Ajoutez une adresse d’usage « Qualité » à la fiche.</span>}
      </div>
      {etat.peutReleve && <div><Bouton taille="sm" variante="primaire" onClick={() => setOuvert(true)}>Préparer le relevé des NC</Bouton></div>}
    </div>
  );
}
